// src/components/sections/GoogleMapsSection.js
import React, { useState, useEffect, useRef } from 'react';

const GoogleMapsSection = ({ content }) => {
  const [mapLoaded, setMapLoaded] = useState(false);
  const [mapInstance, setMapInstance] = useState(null);
  const [mapError, setMapError] = useState(false);
  const mapContainerRef = useRef(null);
  
  // Extract props from content or use defaults
  const title = content?.title || '';
  const mapHeight = content?.height || "400px";
  const mapZoom = content?.zoom || 15;
  const mapType = content?.mapType || 'roadmap';
  const placeholderImage = content?.placeholderImage || "/images/map-placeholder.jpg";
  const showControls = content?.showControls !== false;
  const allowFullscreen = content?.allowFullscreen !== false;
  const allowStreetView = content?.allowStreetView !== false;
  const loadMethod = content?.loadMethod || 'embed';
  const markers = content?.markers || [];
  const showTraffic = content?.showTraffic === true;
  const showInfoWindowsOnLoad = content?.showInfoWindowsOnLoad === true;
  const mapStyle = content?.mapStyle || 'default';
  const customMapStyle = content?.customMapStyle || '[]';
  const borderRadius = content?.borderRadius || 'rounded-xl';
  const shadowStyle = content?.shadowStyle || 'shadow-lg';
  const [apiKey, setApiKey] = useState(null);

  const address = content?.address !== undefined 
  ? content.address 
  : (process.env.REACT_APP_COMPANY_ADDRESS || '102-1127 14th St W, North Vancouver, BC, V7P 1J9');

  // Get API key from environment
  // const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  // Fetch the API key on component mount
    useEffect(() => {
      const fetchApiKey = async () => {
        try {
          const response = await fetch('/api/config/maps');
          const data = await response.json();
          setApiKey(data.googleMapsApiKey);
        } catch (error) {
          console.error('Error fetching Google Maps API key:', error);
        }
      };
      fetchApiKey();
    }, []);


  // Load Google Maps JavaScript API
  useEffect(() => {
    if (loadMethod !== 'javascript' || !apiKey || mapLoaded) return;
    
    const loadGoogleMapsApi = () => {
      if (window.google && window.google.maps) {
        initializeMap();
        return;
      }
      
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = initializeMap;
      script.onerror = () => {
        console.error('Failed to load Google Maps API');
        setMapError(true);
      };
      document.head.appendChild(script);
    };
    
    loadGoogleMapsApi();
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiKey, loadMethod]);
  
  // Initialize map
  const initializeMap = () => {
    if (!window.google || !window.google.maps || !mapContainerRef.current) {
      setMapError(true);
      return;
    }
    
    // Set up map styles
    let mapStyles = [];
    if (mapStyle !== 'default' && mapStyle !== 'custom') {
      // Predefined styles
      const styles = {
        silver: [
          { elementType: 'geometry', stylers: [{ color: '#f5f5f5' }] },
          { elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
          { elementType: 'labels.text.stroke', stylers: [{ color: '#f5f5f5' }] },
          /* Additional styles for Silver theme */
        ],
        retro: [
          { elementType: 'geometry', stylers: [{ color: '#ebe3cd' }] },
          { elementType: 'labels.text.fill', stylers: [{ color: '#523735' }] },
          { elementType: 'labels.text.stroke', stylers: [{ color: '#f5f1e6' }] },
          /* Additional styles for Retro theme */
        ],
        dark: [
          { elementType: 'geometry', stylers: [{ color: '#212121' }] },
          { elementType: 'labels.text.stroke', stylers: [{ color: '#212121' }] },
          { elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
          /* Additional styles for Dark theme */
        ],
        night: [
          { elementType: 'geometry', stylers: [{ color: '#242f3e' }] },
          { elementType: 'labels.text.stroke', stylers: [{ color: '#242f3e' }] },
          { elementType: 'labels.text.fill', stylers: [{ color: '#746855' }] },
          /* Additional styles for Night theme */
        ],
        aubergine: [
          { elementType: 'geometry', stylers: [{ color: '#1d2c4d' }] },
          { elementType: 'labels.text.fill', stylers: [{ color: '#8ec3b9' }] },
          { elementType: 'labels.text.stroke', stylers: [{ color: '#1a3646' }] },
          /* Additional styles for Aubergine theme */
        ]
      };
      
      mapStyles = styles[mapStyle] || [];
    } else if (mapStyle === 'custom') {
      // Parse custom JSON style
      try {
        mapStyles = JSON.parse(customMapStyle);
      } catch (error) {
        console.error('Error parsing custom map style:', error);
      }
    }

    // Create map options
    const mapOptions = {
      zoom: mapZoom,
      mapTypeId: mapType,
      mapTypeControl: showControls,
      streetViewControl: allowStreetView && showControls,
      fullscreenControl: allowFullscreen && showControls,
      zoomControl: showControls,
      styles: mapStyles
    };

    // Create map instance
    const map = new window.google.maps.Map(mapContainerRef.current, mapOptions);
    setMapInstance(map);
    
    // Set center based on address or first marker
    if (markers.length > 0 && markers[0].lat && markers[0].lng) {
      // Center on first marker with coordinates
      const firstMarker = markers[0];
      map.setCenter({
        lat: parseFloat(firstMarker.lat),
        lng: parseFloat(firstMarker.lng)
      });
    } else {
      // Geocode the address
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ address }, (results, status) => {
        if (status === 'OK' && results[0]) {
          map.setCenter(results[0].geometry.location);
          
          // Add a default marker if no markers are defined
          if (markers.length === 0) {
            addMarker(map, {
              address,
              lat: results[0].geometry.location.lat(),
              lng: results[0].geometry.location.lng(),
              title: 'Our Location'
            });
          }
        } else {
          console.error('Geocoding failed:', status);
          // Set default center
          map.setCenter({ lat: 49.3209, lng: -123.0947 }); // Default North Vancouver
        }
      });
    }
    
    // Add markers from content configuration
    markers.forEach(markerData => addMarker(map, markerData));
    
    // Add traffic layer if enabled
    if (showTraffic) {
      const trafficLayer = new window.google.maps.TrafficLayer();
      trafficLayer.setMap(map);
    }
    
    setMapLoaded(true);
  };
  
  // Helper function to add a marker to the map
  const addMarker = (map, markerData) => {
    const createMarker = (position) => {
      // Create marker options
      const markerOptions = {
        map,
        position,
        title: markerData.title || 'Location'
      };
      
      // Add custom icon if specified
      if (markerData.iconUrl) {
        markerOptions.icon = {
          url: markerData.iconUrl,
          scaledSize: new window.google.maps.Size(32, 32)
        };
      }
      
      // Create the marker
      const marker = new window.google.maps.Marker(markerOptions);
      
      // Add info window if there's a description
      if (markerData.description) {
        const infoWindow = new window.google.maps.InfoWindow({
          content: `
            <div class="p-2">
              <h3 class="font-medium">${markerData.title || 'Location'}</h3>
              <p>${markerData.description}</p>
            </div>
          `
        });
        
        // Show info window when marker is clicked
        marker.addListener('click', () => {
          infoWindow.open(map, marker);
        });
        
        // Auto-open info window if configured
        if (showInfoWindowsOnLoad) {
          infoWindow.open(map, marker);
        }
      }
      
      return marker;
    };
    
    // If coordinates are provided, create marker directly
    if (markerData.lat && markerData.lng) {
      createMarker({
        lat: parseFloat(markerData.lat),
        lng: parseFloat(markerData.lng)
      });
    } 
    // Otherwise, geocode the address
    else if (markerData.address) {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ address: markerData.address }, (results, status) => {
        if (status === 'OK' && results[0]) {
          createMarker(results[0].geometry.location);
        } else {
          console.error(`Geocoding failed for address "${markerData.address}":`, status);
        }
      });
    }
  };
  
  // Apply custom CSS if provided
  useEffect(() => {
    if (content?.customCss) {
      const styleElement = document.createElement('style');
      styleElement.textContent = content.customCss;
      document.head.appendChild(styleElement);
      
      return () => {
        document.head.removeChild(styleElement);
      };
    }
  }, [content?.customCss]);

  // Handle different rendering methods
  const renderMap = () => {
    if (!apiKey) {
      return (
        <div className="w-full" style={{ height: mapHeight }}>
          <img 
            src={placeholderImage}
            alt="Map Placeholder" 
            className="w-full h-full object-cover"
          />
          <div className="p-4 bg-yellow-100 text-yellow-800 text-center">
            Google Maps API key is not configured. Please set REACT_APP_GOOGLE_MAPS_API_KEY in your environment.
          </div>
        </div>
      );
    }
    
    if (loadMethod === 'embed') {
      // Simple embed method
      return (
        <iframe 
          title="Location Map"
          src={`https://www.google.com/maps/embed/v1/place?key=${apiKey}&q=${encodeURIComponent(address)}&zoom=${mapZoom}&maptype=${mapType}`}
          width="100%" 
          height={mapHeight}
          frameBorder="0" 
          style={{ border: 0 }}
          allowFullScreen={allowFullscreen ? "" : undefined}
          aria-hidden="false"
          tabIndex="0"
          onLoad={() => setMapLoaded(true)}
          onError={() => setMapError(true)}
        ></iframe>
      );
    } else {
      // JavaScript API method
      return (
        <div 
          ref={mapContainerRef}
          style={{ height: mapHeight, width: '100%' }}
          className="google-map-container"
        ></div>
      );
    }
  };

  return (
    <div className="google-maps-section">
      {title && <h2 className="text-2xl font-bold mb-4">{title}</h2>}
      
      <div className={`overflow-hidden ${borderRadius} ${shadowStyle} bg-gray-200`}>
        {mapError ? (
          <div className="w-full" style={{ height: mapHeight }}>
            <img 
              src={placeholderImage}
              alt="Map Placeholder" 
              className="w-full h-full object-cover"
            />
            <div className="p-4 bg-red-100 text-red-800 text-center">
              Failed to load Google Maps. Please check your connection and API key.
            </div>
          </div>
        ) : (
          <>
            {!mapLoaded && loadMethod === 'javascript' && (
              <div className="w-full absolute" style={{ height: mapHeight }}>
                <img 
                  src={placeholderImage}
                  alt="Map Placeholder" 
                  className="w-full h-full object-cover"
                />
              </div>
            )}
            {renderMap()}
          </>
        )}
      </div>
    </div>
  );
};

export default GoogleMapsSection;







// // src/components/sections/GoogleMapsSection.js
// import React, { useState, useEffect } from 'react';

// const GoogleMapsSection = ({ content }) => {
//   const [mapLoaded, setMapLoaded] = useState(false);
//   // const [apiKey, setApiKey] = useState(null);
  
//   // Extract address from content or use default
//   const address = process.env.REACT_APP_COMPANY_ADDRESS || content?.address ;
//   const mapHeight = content?.height || "400px";
//   const placeholderImage = content?.placeholderImage || "/images/map.jpg";

//   // const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

//   // Fetch the API key on component mount
//   useEffect(() => {
//     const fetchApiKey = async () => {
//       try {
//         const response = await fetch('/api/config/maps');
//         const data = await response.json();
//         setApiKey(data.googleMapsApiKey);
//       } catch (error) {
//         console.error('Error fetching Google Maps API key:', error);
//       }
//     };
//     fetchApiKey();
//   }, []);

  
//   return (
//     <div className="bg-gray-200 rounded-xl overflow-hidden shadow-lg" style={{ padding: 0, margin: 0 }}>
//       {apiKey ? (
//         <>
//           {/* {!mapLoaded && (
//             <div className="w-full" style={{ height: mapHeight }}>
//               <img 
//                 src={placeholderImage}
//                 alt="Location Map" 
//                 className="w-full h-full object-cover"
//               />
//             </div>
//           )} */}
//           <iframe 
//             title="Location Map"
//             src={`https://www.google.com/maps/embed/v1/place?key=${apiKey}&q=${encodeURIComponent(address)}`}
//             width="100%" 
//             height={mapHeight}
//             frameBorder="0" 
//             style={{ border: 0, display: mapLoaded ? 'block' : 'none' }}
//             allowFullScreen=""
//             aria-hidden="false"
//             tabIndex="0"
//             onLoad={() => setMapLoaded(true)}
//           ></iframe>
//         </>
//       ) : (
//         <div className="w-full" style={{ height: mapHeight }}>
//           <img 
//             src={placeholderImage}
//             alt="Location Map" 
//             className="w-full h-full object-cover"
//           />
//         </div>
//       )}
//     </div>
//   );
// };

// export default GoogleMapsSection;